import { FileDownload, PrintOutlined } from "@mui/icons-material";
import { Box, Menu, MenuItem } from "@mui/material";
import { type FC, type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../SystemButton/SystemButton";

export type DownloadFormat = "xlsx" | "pdf";

const FORMAT_LABEL_KEY: Record<DownloadFormat, string> = {
	xlsx: "download.excel",
	pdf: "download.pdf",
};

export type DownloadFormatButtonProps = {
	/** Offered formats, in menu order. A single entry renders one direct button with no menu. */
	formats: DownloadFormat[];
	onDownload: (format: DownloadFormat) => void;
	isDownloading?: boolean;
	disabled?: boolean;
	/**
	 * Why the button is disabled, shown as a tooltip for as long as it is.
	 *
	 * `SystemButton` wraps a tooltip target in a Box, so hover still registers on a disabled button —
	 * which is the only chance a user gets to learn why clicking does nothing.
	 */
	disabledReason?: ReactNode;
	/** Overrides the label. Defaults to `t("download")`, or the sole format's own label. */
	label?: ReactNode;
	dataTestIdPrefix: string;
	/**
	 * Renders a "Print" button beside Download when provided. Each dialog still owns building and
	 * printing its own PDF blob (there is no shared print pipeline across Daily/Weekly/Monthly) — this
	 * only renders the control and wires its click/loading/disabled state, sharing Download's.
	 */
	onPrint?: () => void;
	isPrinting?: boolean;
};

/**
 * The download control every export dialog was hand-rolling: one button for a single format, or a
 * button opening a format menu for several. Optionally paired with a "Print" button (see `onPrint`).
 */
export const DownloadFormatButton: FC<DownloadFormatButtonProps> = ({
	formats,
	onDownload,
	isDownloading,
	disabled,
	disabledReason,
	label,
	dataTestIdPrefix,
	onPrint,
	isPrinting,
}) => {
	const { t } = useTranslation();
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	// Print and Download build/print the SAME underlying PDF from the same on-screen data — running both
	// at once (or the same one twice) serves no purpose and risks overlapping iframe/print-dialog state,
	// so each one's own in-flight operation disables the other too, not just itself. A button disabled
	// this way still needs its own explanation: `disabledReason` only covers the READINESS reason, so a
	// button greyed out purely because its sibling is mid-operation would otherwise show no tooltip at all.
	const busy = Boolean(disabled) || Boolean(isDownloading) || Boolean(isPrinting);
	const tooltip = disabled ? disabledReason : busy ? t("download.export.in.progress") : undefined;

	const closeMenu = useCallback(() => setMenuAnchor(null), []);

	const handlePick = useCallback(
		(format: DownloadFormat) => {
			closeMenu();
			onDownload(format);
		},
		[closeMenu, onDownload],
	);

	const soleFormat = formats.length === 1 ? formats[0] : undefined;

	const printButton = onPrint ? (
		<SystemButton
			data-testid={`${dataTestIdPrefix}-print`}
			buttonVariant="secondary"
			size="small"
			disabled={busy}
			loading={isPrinting}
			tooltip={tooltip}
			onClick={onPrint}
			leadingIcon={<PrintOutlined />}
		>
			{t("print")}
		</SystemButton>
	) : null;

	if (soleFormat) {
		const downloadButton = (
			<SystemButton
				data-testid={`${dataTestIdPrefix}-download`}
				disabled={busy}
				loading={isDownloading}
				tooltip={tooltip}
				onClick={() => onDownload(soleFormat)}
				leadingIcon={<FileDownload />}
			>
				{label ?? t(FORMAT_LABEL_KEY[soleFormat])}
			</SystemButton>
		);
		if (!printButton) return downloadButton;
		return (
			<Box sx={{ display: "flex", gap: 1 }}>
				{printButton}
				{downloadButton}
			</Box>
		);
	}

	const downloadMenu = (
		<>
			<SystemButton
				data-testid={`${dataTestIdPrefix}-download`}
				disabled={busy}
				loading={isDownloading}
				tooltip={tooltip}
				onClick={(event) => setMenuAnchor(event.currentTarget)}
				leadingIcon={<FileDownload />}
				aria-haspopup="menu"
				aria-expanded={Boolean(menuAnchor)}
			>
				{label ?? t("download")}
			</SystemButton>
			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={closeMenu}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				transformOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				{formats.map((format) => (
					<MenuItem key={format} data-testid={`${dataTestIdPrefix}-download-${format}`} onClick={() => handlePick(format)}>
						{t(FORMAT_LABEL_KEY[format])}
					</MenuItem>
				))}
			</Menu>
		</>
	);

	if (!printButton) return downloadMenu;
	return (
		<Box sx={{ display: "flex", gap: 1 }}>
			{printButton}
			{downloadMenu}
		</Box>
	);
};
